import { Card, Grid, Icon, IconButton, InputLabel, Tooltip, Autocomplete, Switch, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { verifyVariable } from "utils/Functions";
import { get, post, postNoResponse } from "utils/WebService";
import StatusFichaCreateEdit from "./StatusFichaCreateEdit";
import { useUser } from "context/UserContext";

export default function StatusFichaConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userPerfils } = useUser();

    const isLiberadoEquipeFilter = userPerfils && userPerfils.some(p => {
        const pName = (p?.nome || p?.ccNome || (typeof p === 'string' ? p : "")).toUpperCase();
        const pCode = p?.tipoPerfil ?? p?.cvCodPerfil ?? p?.cvTipo;
        return (
            pCode === 1 || pCode === 4 || pCode === 6 ||
            pName.includes("ADMIN") || pName.includes("SUPORTE") || pName.includes("GESTOR") || pName.includes("GERENTE")
        );
    });

    const [equipesList, setEquipesList] = useState([]);

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [{ id: 'cvAtivo', value: true }],
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);

    const [filters, setFilters] = useState({
        ccNome: '',
        cvAtivo: true,
        forca: '',
        equipeId: ''
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    useEffect(() => {
        get("/equipe/list-all").then((data) => {
            setEquipesList(data?.list || data?.data || (Array.isArray(data) ? data : []));
        }).catch(() => setEquipesList([]));
        fetchData({ ...tableState });
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered }) => {
        setLoadingTable(true);
        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
        };
        tableState.current = updatedTableState;

        post("/status-ficha/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
        }).then(({ data: resultList, pages, recordsTotal }) => {
            let list = resultList || [];
            if (filters.equipeId) {
                list = list.filter(item => {
                    if (!item.equipes || item.equipes.length === 0) return true;
                    return item.equipes.some(eq => String(eq.id || eq.cvId) === String(filters.equipeId));
                });
            }
            setPages(pages);
            setData(list);
            setRecordsTotal(list.length);
            setLoadingTable(false);
            setAplicar(false);
        }).catch(() => {
            setLoadingTable(false);
            setAplicar(false);
        });
    }, [filters.equipeId]);

    const backFunction = () => {
        fetchData({ ...tableState });
        setIsCreateEdit(false);
        setSelectedId(null);
    };

    const handleToggleAtivo = (row, newStatus) => {
        const rowId = row.id !== undefined ? row.id : row.cvId;
        const payload = {
            id: rowId,
            ccNome: row.ccNome,
            cvAtivo: newStatus,
            forca: row.forca,
            equipes: row.equipes || null
        };
        postNoResponse("/status-ficha/save", payload)
            .then(() => {
                setSbMessage(`Status "${row.ccNome}" ${newStatus ? "ativado" : "inativado"} com sucesso!`);
                setSuccessSB(true);
                fetchData({ ...tableState });
            })
            .catch((err) => {
                const errorMsg = err?.response?.data?.message || err?.data?.message || err?.message || "Erro ao alterar status.";
                setSbMessage(errorMsg);
                setErrorSB(true);
            });
    };

    const handleDeleteClick = (row) => {
        setItemToDelete(row);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (!itemToDelete) return;
        const rowId = itemToDelete.id !== undefined ? itemToDelete.id : itemToDelete.cvId;
        postNoResponse(`/status-ficha/delete/${rowId}`)
            .then(() => {
                setSbMessage(`Status "${itemToDelete.ccNome}" excluído com sucesso!`);
                setSuccessSB(true);
                fetchData({ ...tableState });
            })
            .catch((err) => {
                const errorMsg = err?.response?.data?.message || err?.data?.message || err?.message || "Não é possível excluir o status pois ele está sendo utilizado em fichas de cobrança.";
                setSbMessage(errorMsg);
                setErrorSB(true);
            })
            .finally(() => {
                setDeleteDialogOpen(false);
                setItemToDelete(null);
            });
    };

    const userColumns = [
        {
            header: "nome",
            accessorKey: "ccNome",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "ativo",
            accessorKey: "cvAtivo",
            cell: info => {
                const row = info.row.original;
                const isChecked = !!info.getValue();
                return (
                    <MDBox display="flex" alignItems="center">
                        <Switch
                            checked={isChecked}
                            onChange={(e) => handleToggleAtivo(row, e.target.checked)}
                            color="info"
                        />
                    </MDBox>
                );
            },
            isSorted: false
        },
        {
            header: "força/peso",
            accessorKey: "forca",
            cell: info => {
                const val = info.getValue();
                let label = "Não configurado";
                if (val === 1) label = "Fraco";
                if (val === 2) label = "Médio";
                if (val === 3) label = "Forte";
                return <DefaultCell>{label}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "ações",
            isSorted: false,
            cell: info => {
                const row = info.row.original;
                return (
                    <MDBox display="flex" alignItems="center">
                        <Tooltip title="Editar">
                            <IconButton
                                onClick={() => {
                                    const rowId = row.id !== undefined ? row.id : row.cvId;
                                    if (rowId !== undefined && rowId !== null) {
                                        setSelectedId(rowId);
                                        setIsCreateEdit(true);
                                    } else {
                                        alert("Erro: O ID não foi retornado pelo servidor.");
                                    }
                                }}>
                                <MDTypography variant="h4">
                                    <Icon color="inherit">edit</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                            <IconButton onClick={() => handleDeleteClick(row)}>
                                <MDTypography variant="h4">
                                    <Icon color="error">delete</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                    </MDBox>
                );
            },
            width: "15%"
        },
    ];

    const handleApplyFilter = () => {
        let filtered = [];
        Object.keys(filters).forEach(filterKey => {
            if (verifyVariable(filters[filterKey])) {
                filtered.push({ id: filterKey, value: filters[filterKey] });
            }
        });
        setAplicar(true);
        fetchData({ ...tableState, pageIndex: 0, filtered });
    };

    const handleCleanFilter = () => {
        setFilters({ ccNome: '', cvAtivo: true, forca: '', equipeId: '' });
        fetchData({ pageSize: 10, pageIndex: 0, sorting: [], filtered: [{ id: 'cvAtivo', value: true }] });
    };

    const equipeOptions = [
        { id: '', nome: 'Todas as Equipes' },
        ...equipesList.map(eq => ({ id: String(eq.id || eq.cvId), nome: eq.nome || eq.ccNome }))
    ];

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <StatusFichaCreateEdit id={selectedId} closeCreateEdit={backFunction} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, background: darkMode ? "#4B4F5C" : "#fafafa", borderColor: darkMode ? "#333" : "#ddd" }}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item xs={12} sm={isLiberadoEquipeFilter ? 2.5 : 3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2, color: darkMode ? "#ffffff" : "inherit" }}>Nome</InputLabel>
                            <MDInput size="small" placeholder="Nome..." value={filters.ccNome} onChange={({ target }) => setFilters({ ...filters, ccNome: target.value })} onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }} fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={isLiberadoEquipeFilter ? 2 : 2.5}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2, color: darkMode ? "#ffffff" : "inherit" }}>Status</InputLabel>
                            <Autocomplete
                                options={[{ label: 'Ambos', value: '' }, { label: 'Ativos', value: true }, { label: 'Inativos', value: false }]}
                                value={[{ label: 'Ambos', value: '' }, { label: 'Ativos', value: true }, { label: 'Inativos', value: false }].find(opt => opt.value === filters.cvAtivo) || { label: 'Ambos', value: '' }}
                                onChange={(event, newValue) => {
                                    setFilters({ ...filters, cvAtivo: newValue ? newValue.value : '' });
                                }}
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                renderInput={(params) => <MDInput {...params} size="small" placeholder="Status..." />}
                                disableClearable
                            />
                        </Grid>
                        <Grid item xs={12} sm={isLiberadoEquipeFilter ? 2 : 2.5}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2, color: darkMode ? "#ffffff" : "inherit" }}>Força/Peso</InputLabel>
                            <Autocomplete
                                options={[
                                    { label: 'Todos', value: '' },
                                    { label: 'Fraco', value: 1 },
                                    { label: 'Médio', value: 2 },
                                    { label: 'Forte', value: 3 }
                                ]}
                                value={[
                                    { label: 'Todos', value: '' },
                                    { label: 'Fraco', value: 1 },
                                    { label: 'Médio', value: 2 },
                                    { label: 'Forte', value: 3 }
                                ].find(opt => opt.value === filters.forca) || { label: 'Todos', value: '' }}
                                onChange={(event, newValue) => {
                                    setFilters({ ...filters, forca: newValue ? newValue.value : '' });
                                }}
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                renderInput={(params) => <MDInput {...params} size="small" placeholder="Força/Peso..." />}
                                disableClearable
                            />
                        </Grid>
                        {isLiberadoEquipeFilter && (
                            <Grid item xs={12} sm={2.5}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2, color: darkMode ? "#ffffff" : "inherit" }}>Equipe</InputLabel>
                                <Autocomplete
                                    options={equipeOptions}
                                    value={equipeOptions.find(opt => opt.id === filters.equipeId) || equipeOptions[0]}
                                    onChange={(event, newValue) => {
                                        setFilters({ ...filters, equipeId: newValue ? newValue.id : '' });
                                    }}
                                    getOptionLabel={(opt) => opt.nome || ''}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                    renderInput={(params) => <MDInput {...params} size="small" placeholder="Equipe..." />}
                                    disableClearable
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={isLiberadoEquipeFilter ? 3 : 4} display="flex" alignItems="center" justifyContent="flex-end" pt={1}>
                            <MDBox display="flex" gap={1} mt={3}>
                                <MDButton 
                                    size="medium" 
                                    variant="contained" 
                                    color="info" 
                                    onClick={() => { setSelectedId(null); setIsCreateEdit(true); }}
                                    sx={{
                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                        color: "#ffffff !important"
                                    }}
                                >
                                    Adicionar
                                </MDButton>
                                <MDButton 
                                    size="medium" 
                                    variant="outlined" 
                                    color="secondary" 
                                    onClick={handleCleanFilter}
                                    sx={{
                                        color: darkMode ? "#ffffff !important" : undefined,
                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                                    }}
                                >
                                    Limpar
                                </MDButton>
                                <MDButton 
                                    size="medium" 
                                    variant="contained" 
                                    color="info" 
                                    onClick={handleApplyFilter}
                                    sx={{
                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                        color: "#ffffff !important"
                                    }}
                                >
                                    Aplicar
                                </MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                <DataTable loading={loadingTable} columnData={userColumns} data={data} fetchData={fetchData} totalPages={pages} totalRecords={recordsTotal} changeFilter={aplicar} tableState={tableState} />
            </Grid>

            {/* Dialog de Confirmação de Exclusão */}
            <Dialog 
                open={deleteDialogOpen} 
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#424550" : "#ffffff",
                        color: darkMode ? "#ffffff" : "inherit"
                    }
                }}
            >
                <DialogTitle sx={{ color: darkMode ? "#ffffff" : "inherit" }}>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color={darkMode ? "white" : "text"}>
                        Tem certeza que deseja excluir o status <strong>{itemToDelete?.ccNome}</strong>?
                    </MDTypography>
                    <br/>
                    <MDTypography variant="caption" color="error">
                        Atenção: Se este status estiver em uso por fichas de cobrança, a exclusão será bloqueada.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton 
                        onClick={() => setDeleteDialogOpen(false)} 
                        variant="outlined" 
                        color="secondary"
                        sx={{
                            color: darkMode ? "#ffffff !important" : undefined,
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                        }}
                    >
                        Cancelar
                    </MDButton>
                    <MDButton onClick={confirmDelete} color="error" variant="contained">
                        Excluir
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Feedback / Notificações */}
            <MDSnackbar
                color={successSB ? "success" : "error"}
                icon={successSB ? "check" : "warning"}
                title={successSB ? "Sucesso" : "Atenção"}
                content={sbMessage}
                dateTime=""
                open={successSB || errorSB}
                onClose={() => { setSuccessSB(false); setErrorSB(false); }}
                close={() => { setSuccessSB(false); setErrorSB(false); }}
                bgWhite
            />
        </Grid>
    );
}
